import { lire, tout, ecrire, supprimer, ecrireLot, demanderPersistance } from './idb.js';

// L'instantané tient sur une seule ligne, sous une clé fixe : il est toujours lu et
// écrit d'un bloc, et c'est justement ce qui garantit qu'on n'aura jamais un effectif
// de mardi à côté de blessures de dimanche.
const CLE_INSTANTANE = 'courant';

export const lireInstantane = () => lire('instantane', CLE_INSTANTANE);

/**
 * Rafraîchir depuis le serveur, sans jamais casser.
 *
 * Le réseau est une amélioration, pas une condition. Si l'appel échoue — et dans un
 * stade il échouera — on rend ce qu'on avait, marqué hors-ligne, et l'interface
 * affiche la date des données au lieu de les présenter comme fraîches.
 */
export async function rafraichirInstantane({ delaiMs = 12000 } = {}) {
  const local = await lireInstantane();
  try {
    const r = await fetch('/api/instantane', {
      signal: AbortSignal.timeout(delaiMs),
      headers: { accept: 'application/json' },
    });
    if (r.status === 401) return { instantane: local ?? null, etat: 'deconnecte' };
    if (!r.ok) throw new Error(`HTTP ${r.status}`);
    const frais = await r.json();
    await ecrire('instantane', frais, CLE_INSTANTANE);
    return { instantane: frais, etat: 'frais' };
  } catch {
    return { instantane: local ?? null, etat: local ? 'hors-ligne' : 'vide' };
  }
}

// ————————————————————————————————————————————————————— écritures de l'utilisateur
//
// Toute écriture suit le même chemin : IndexedDB d'abord, file ensuite, réseau au
// mieux. L'ordre n'est pas négociable — écrire d'abord sur le réseau, c'est perdre la
// saisie quand il n'y a pas de réseau, c'est-à-dire précisément là où l'application
// est censée servir.

const maintenant = () => Date.now();
const identifiant = () => (crypto.randomUUID ? crypto.randomUUID() : `${Date.now()}-${Math.random().toString(36).slice(2)}`);

async function enfiler(genre, charge) {
  await ecrire('sortie', { id: identifiant(), genre, charge, creeA: maintenant() });
  // Tentative opportuniste : si le réseau est là, la file part tout de suite et
  // l'utilisateur n'a rien à faire. Si elle échoue, elle reste en base.
  vidangerFile().catch(() => {});
}

/**
 * Enregistrer un match — l'entité unique.
 *
 * Composition et journal vivaient séparément, joints par l'identifiant de rencontre du
 * fournisseur. Sans calendrier, cet identifiant valait « libre » pour tout le monde et
 * le deuxième match écrasait le journal du premier, sans un mot. Un match porte
 * maintenant son propre identifiant et tout son contenu.
 */
export async function sauverMatch(m) {
  const enregistrement = {
    id: m.id ?? identifiant(),
    date: m.date ?? new Date().toISOString().slice(0, 10),
    adversaire: m.adversaire ?? null,
    domicile: m.domicile !== false,
    competition: m.competition ?? null,
    formation: m.formation,
    places: (m.places ?? []).map((p) => ({ index: p.index, code: p.code, joueurId: p.joueurId })),
    evenements: m.evenements ?? [],
    etat: m.etat ?? 'probable',
    cree_a: m.cree_a ?? maintenant(),
    maj_a: maintenant(),
    supprime_a: null,
  };
  await ecrire('matchs', enregistrement);
  await enfiler('match', enregistrement);
  return enregistrement;
}

export async function supprimerMatch(id) {
  const existant = await lire('matchs', id);
  if (!existant) return;
  // Marqué supprimé plutôt que retiré : un effacement doit voyager jusqu'au serveur,
  // et une ligne absente de la file ne dit rien à personne.
  const efface = { ...existant, supprime_a: maintenant(), maj_a: maintenant() };
  await ecrire('matchs', efface);
  await enfiler('match', efface);
}

export const lireMatch = (id) => lire('matchs', id);

/**
 * LA COMPO EN COURS, entre deux lancements.
 *
 * Un match n'est enregistré qu'à partir de « C'est la compo » ; avant, la composition
 * n'existe qu'en mémoire. On prépare souvent la veille, on ferme, on revient le
 * lendemain — retrouver un terrain repré-rempli plutôt que ses propres corrections est
 * un travail refait pour rien.
 *
 * ÉCRITE AUX DEUX ENDROITS, et localStorage est le maître.
 *
 * IndexedDB seule ne suffisait pas : elle est asynchrone, et iOS tue l'application
 * sans prévenir. Vider le terrain puis quitter dans la seconde perdait l'écriture, et
 * le pré-remplissage revenait au lancement suivant — ce qui donne exactement
 * l'impression que rien n'est sauvegardé. `localStorage.setItem` est déjà terminé
 * quand la ligne retourne ; IndexedDB reste la copie durable, hors de portée du quota
 * de cinq mégaoctets.
 */
const CLE_BROUILLON = 'aja.brouillon';

export function sauverBrouillon(compo) {
  const charge = { ...compo, ecritA: Date.now() };
  try { localStorage.setItem(CLE_BROUILLON, JSON.stringify(charge)); } catch { /* mode privé */ }
  return ecrire('reglages', charge, 'compo-courante').catch(() => null);
}

export async function lireBrouillon() {
  let rapide = null;
  try {
    const brut = localStorage.getItem(CLE_BROUILLON);
    if (brut) rapide = JSON.parse(brut);
  } catch { /* illisible : on se rabat sur IndexedDB */ }

  const durable = await lire('reglages', 'compo-courante').catch(() => null);
  // Le plus récemment écrit gagne : localStorage l'est presque toujours, mais si le
  // navigateur l'a vidé, la copie durable prend le relais.
  if (!rapide) return durable ?? null;
  if (!durable) return rapide;
  return (durable.ecritA ?? 0) > (rapide.ecritA ?? 0) ? durable : rapide;
}

export function oublierBrouillon() {
  try { localStorage.removeItem(CLE_BROUILLON); } catch { /* sans effet */ }
  return supprimer('reglages', 'compo-courante').catch(() => null);
}

export const listerMatchs = async () => (await tout('matchs'))
  .filter((m) => !m.supprime_a)
  .sort((a, b) => String(b.date ?? '').localeCompare(String(a.date ?? '')) || b.maj_a - a.maj_a);

export async function sauverNote(joueurId, texte) {
  const n = { joueurId, texte, maj_a: maintenant() };
  await ecrire('notes', n);
  await enfiler('note', { joueur_id: joueurId, texte, maj_a: n.maj_a });
  return n;
}

export const lireNote = (joueurId) => lire('notes', joueurId);

// ————————————————————————————————————————————————————— la file
/**
 * Rejouer la file d'écritures.
 *
 * Safari iOS n'a ni Background Sync ni Periodic Sync, et rien ne s'y exécute quand
 * l'application n'est pas au premier plan. La synchronisation se fait donc quand
 * l'utilisateur regarde l'écran, et à aucun autre moment : c'est `brancherSynchro`
 * qui pose les déclencheurs.
 *
 * Le serveur ignore sans erreur une écriture plus ancienne que ce qu'il détient, donc
 * rejouer deux fois la même file est sans conséquence. On ne vide la file qu'APRÈS
 * l'accusé de réception — l'inverse perdrait la saisie sur une coupure au mauvais
 * moment.
 */
let vidangeEnCours = null;

export function vidangerFile() {
  if (vidangeEnCours) return vidangeEnCours;
  vidangeEnCours = (async () => {
    const file = await tout('sortie');
    if (!file.length) return { envoyees: 0 };

    const corps = { matchs: [], notes: [] };
    for (const e of file) {
      if (e.genre === 'match') corps.matchs.push(e.charge);
      else if (e.genre === 'note') corps.notes.push(e.charge);
    }

    const r = await fetch('/api/synchro', {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify(corps),
      signal: AbortSignal.timeout(10000),
    });
    if (!r.ok) throw new Error(`synchro HTTP ${r.status}`);

    for (const e of file) await supprimer('sortie', e.id);
    return { envoyees: file.length, etat: await r.json() };
  })().finally(() => { vidangeEnCours = null; });
  return vidangeEnCours;
}

export const fileEnAttente = async () => (await tout('sortie')).length;

/**
 * Les cinq déclencheurs de synchronisation.
 *
 * `visibilitychange` est le principal sur iOS : une application installée n'est
 * presque jamais démarrée à froid, elle est réveillée. S'en remettre au seul
 * démarrage, c'est une file qui part trois jours plus tard.
 *
 * L'événement `online` est un bonus et non la base : sur iOS il ment régulièrement,
 * et `navigator.onLine` vaut `true` sur le wifi captif d'un stade qui ne route rien.
 */
let branche = false;

export function brancherSynchro() {
  // Idempotent : appelée au démarrage puis après une connexion, elle poserait sinon
  // deux jeux d'écouteurs et deux intervalles, qui videraient la file en double.
  if (branche) return;
  branche = true;

  const essayer = () => { vidangerFile().catch(() => {}); };

  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible') essayer();
  });
  window.addEventListener('online', essayer);

  // Uniquement au premier plan : un intervalle qui tourne en arrière-plan ne
  // s'exécuterait pas sur iOS, et userait la batterie partout ailleurs.
  let minuteur = null;
  const rythmer = () => {
    clearInterval(minuteur);
    if (document.visibilityState === 'visible') minuteur = setInterval(essayer, 30000);
  };
  document.addEventListener('visibilitychange', rythmer);
  rythmer();

  essayer();
  demanderPersistance();
}

/** Recaler le local sur ce que le serveur détient (première ouverture, autre appareil). */
export async function recalerDepuisServeur() {
  try {
    const r = await fetch('/api/miennes', { signal: AbortSignal.timeout(6000) });
    if (!r.ok) return false;
    const etat = await r.json();
    if (etat.matchs?.length) await ecrireLot('matchs', etat.matchs);
    if (etat.notes?.length) {
      await ecrireLot('notes', etat.notes.map((n) => ({ joueurId: n.joueur_id, texte: n.texte, maj_a: n.maj_a })));
    }
    return true;
  } catch {
    return false;
  }
}
